import moment from 'moment';
import { ProjectStackDocument } from '.';
import { base64ToBytes, bytesToBase64 } from './transform';
import {
  Project,
  EntityType,
  FieldType,
  StringFieldType,
  ProjectStackSavedData,
  SUPPORTED_DATE_FORMATS,
} from './types';

const CONTACT_ENTITY_TYPES: string[] = [
  EntityType.CONTACT,
  EntityType.CLIO_CONTACT,
].map((entityType) => entityType.toLowerCase());

export const isContactEntityType = (entityType?: string): boolean =>
  !!entityType && CONTACT_ENTITY_TYPES.includes(entityType.toLowerCase());

export const isMultipleChoiceFieldType = (
  fieldType: FieldType | StringFieldType,
) =>
  fieldType === FieldType.MULTIPLE_CHOICE ||
  fieldType === FieldType.BATCH_SELECT ||
  fieldType === 'BATCH_SELECT' ||
  fieldType === 'MULTIPLE_CHOICE';

export const isPatchSelectType = (fieldType: FieldType | StringFieldType) =>
  [FieldType.BATCH_SELECT, StringFieldType.BATCH_SELECT].includes(fieldType);

export const isDateFieldType = (fieldType: FieldType | StringFieldType) =>
  [FieldType.DATE, StringFieldType.DATE].includes(fieldType);

export const FORMAT_OVERRIDE_PREFIX = 'fmt:';

// Reformat `value` into `targetFormat`; `sourceFormat` disambiguates parsing.
export const reformatDateValue = (
  value: string,
  targetFormat: string,
  sourceFormat?: string,
): string | null => {
  if (!value || !targetFormat) return null;
  const primary = sourceFormat ?? targetFormat;
  let parsed = moment(value, primary, true);
  if (!parsed.isValid()) parsed = moment(value, SUPPORTED_DATE_FORMATS, true);
  if (!parsed.isValid()) parsed = moment(value, SUPPORTED_DATE_FORMATS, false);
  return parsed.isValid() ? parsed.format(targetFormat) : null;
};

// This function transforms all references to a projects fields
// so that special characters won't get in the way of the form
// We need to run the form values through an "untransformer" to get the
// correct field values back (see unsantisizeProjectRecord)
export const transformProjectFields = (project: Project): Project => {
  const documents = project.stack_data?.documents ?? [];
  const fields = project.stack_data?.fields ?? [];
  const groups = project.stack_data?.groups ?? [];

  const transformedDocuments = documents.map((d) => ({
    ...d,
    document_fields: Object.entries(d.document_fields ?? {}).reduce<
      ProjectStackDocument['document_fields']
    >((acc, [key, value]) => {
      acc[sanitizeFieldName(key)] = value;
      return acc;
    }, {}),
  }));

  // Create a flat list of document_fields from all documents
  project.document_fields = transformedDocuments.flatMap((d) =>
    Object.entries(d.document_fields).map(([key, value]) => ({ [key]: value })),
  );

  // Continue the rest of the transformations for stack_data and stack_saved_data
  const transformedProject = {
    ...project,
    stack_data: {
      ...project.stack_data,
      documents: transformedDocuments,
      fields: fields.map((f) => ({
        ...f,
        fid: sanitizeFieldName(f.fid),
      })),
      groups: groups.map((g) => ({
        ...g,
        fields: (g.fields ?? []).map(sanitizeFieldName),
      })),
    },
    stack_saved_data: Object.entries(
      project.stack_saved_data ?? {},
    ).reduce<ProjectStackSavedData>((acc, [key, value]) => {
      if (key.startsWith(FORMAT_OVERRIDE_PREFIX)) {
        // Format override: encode the field name portion after the prefix
        acc[
          `${FORMAT_OVERRIDE_PREFIX}${sanitizeFieldName(
            key.slice(FORMAT_OVERRIDE_PREFIX.length),
          )}`
        ] = value;
      } else {
        acc[sanitizeFieldName(key)] = value;
      }
      return acc;
    }, {}),
  };

  return transformedProject;
};

export const sanitizeFieldName = (name: string) =>
  bytesToBase64(new TextEncoder().encode(name));

export const unsanitizeProjectFieldName = (name: string) =>
  new TextDecoder().decode(base64ToBytes(name));

export const unsanitizeProjectRecord = (record: Record<string, any>) =>
  Object.entries(record).reduce<Record<string, any>>((acc, [key, value]) => {
    if (key.startsWith(FORMAT_OVERRIDE_PREFIX)) {
      // Format override: decode the field name portion after the prefix
      acc[
        `${FORMAT_OVERRIDE_PREFIX}${unsanitizeProjectFieldName(
          key.slice(FORMAT_OVERRIDE_PREFIX.length),
        )}`
      ] = value;
    } else {
      acc[unsanitizeProjectFieldName(key)] = value;
    }
    return acc;
  }, {});
